import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { 
  FiUpload, FiCheck, FiX, FiRefreshCw, FiImage, 
  FiZoomIn, FiClock, FiEdit, FiSend, FiDownload,
  FiMessageSquare, FiUser, FiChevronLeft, FiChevronRight, FiPlay
} from 'react-icons/fi';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Design format configurations with demo backgrounds
const DESIGN_FORMATS = {
  portrait: {
    label: 'Portrait',
    ratio: '3/4',
    width: 300,
    height: 400,
    demoImage: 'https://images.unsplash.com/photo-1511285560929-80b456fea0bc?w=300&h=400&fit=crop',
    description: 'Format vertical pour photos classiques'
  },
  landscape: {
    label: 'Paysage',
    ratio: '4/3',
    width: 400,
    height: 300,
    demoImage: 'https://images.unsplash.com/photo-1519741497674-611481863552?w=400&h=300&fit=crop',
    description: 'Format horizontal'
  },
  bandelette: {
    label: 'Bandelette',
    ratio: '1/3',
    width: 200,
    height: 600,
    demoImage: 'https://images.unsplash.com/photo-1529634806980-85c3dd6d34ac?w=200&h=600&fit=crop',
    description: 'Bande photo style photomaton'
  },
  video360: {
    label: 'Vidéo 360',
    ratio: '16/9',
    width: 640,
    height: 360,
    demoVideo: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
    description: 'Cadre pour vidéo 360°'
  },
  livreor: {
    label: "Livre d'Or",
    ratio: '16/9',
    width: 640,
    height: 360,
    demoImage: 'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=640&h=360&fit=crop',
    description: 'Cadre pour messages vidéo'
  }
};

const StatusBadge = ({ status }) => {
  const configs = {
    pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800', icon: FiClock },
    approved: { label: 'Approuvé', color: 'bg-green-100 text-green-800', icon: FiCheck },
    revision_requested: { label: 'Révision demandée', color: 'bg-orange-100 text-orange-800', icon: FiRefreshCw },
    in_progress: { label: 'En cours', color: 'bg-blue-100 text-blue-800', icon: FiEdit },
  };
  const config = configs[status] || configs.pending;
  const Icon = config.icon;
  return (
    <Badge className={`${config.color} flex items-center gap-1`}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
};

// Design preview component with demo background
const DesignPreview = ({ designUrl, format, showDemo = true }) => {
  const formatConfig = DESIGN_FORMATS[format] || DESIGN_FORMATS.portrait;
  const isVideo = format === 'video360';
  
  return (
    <div 
      className="relative mx-auto overflow-hidden rounded-lg shadow-2xl bg-gray-900"
      style={{ 
        width: formatConfig.width > 400 ? '100%' : formatConfig.width,
        maxWidth: formatConfig.width,
        aspectRatio: formatConfig.ratio
      }}
    >
      {/* Demo background */}
      {showDemo && (
        isVideo ? (
          <iframe
            src={formatConfig.demoVideo}
            className="absolute inset-0 w-full h-full"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title="Demo video"
          />
        ) : (
          <img 
            src={formatConfig.demoImage} 
            alt="Demo" 
            className="absolute inset-0 w-full h-full object-cover"
          />
        )
      )}
      
      {/* Design overlay (PNG with transparency) */}
      {designUrl && (
        <img 
          src={designUrl} 
          alt="Design frame" 
          className="absolute inset-0 w-full h-full object-contain pointer-events-none"
          style={{ mixBlendMode: 'normal' }}
        />
      )}
      
      {/* Play button overlay for video */}
      {isVideo && !showDemo && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
          <FiPlay className="w-16 h-16 text-white" />
        </div>
      )}
    </div>
  );
};

export default function DesignManager({ reservationId, reservation, onUpdate }) {
  const [loading, setLoading] = useState(true);
  const [designs, setDesigns] = useState([]); // Array of designs per format
  const [uploading, setUploading] = useState(false);
  
  // Upload state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedFormat, setSelectedFormat] = useState('portrait');
  const [uploadNotes, setUploadNotes] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [emailMessage, setEmailMessage] = useState('');
  
  // Preview state
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewDesign, setPreviewDesign] = useState(null);

  // Get required models count from reservation
  const requiredModels = reservation?.options?.models_count || reservation?.models_count || 1;
  
  // Get allowed formats based on product type
  const getAllowedFormats = () => {
    const productName = reservation?.product_name?.toLowerCase() || '';
    if (productName.includes('360') || productName.includes('videobooth')) {
      return ['video360', 'portrait', 'landscape'];
    }
    if (productName.includes("livre d'or") || productName.includes('livreor')) {
      return ['livreor', 'portrait'];
    }
    // Default Photobooth formats
    return ['portrait', 'landscape', 'bandelette'];
  };
  
  const allowedFormats = getAllowedFormats();

  const loadDesigns = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/designs/${reservationId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDesigns(data.designs || []);
      }
    } catch (error) {
      console.error('Error loading designs:', error);
    } finally {
      setLoading(false);
    }
  }, [reservationId]);

  useEffect(() => {
    loadDesigns();
  }, [loadDesigns]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Fichier trop volumineux (max 10MB)');
        return;
      }
      if (!file.type.includes('png')) {
        toast.warning('Pour un meilleur résultat, utilisez un fichier PNG avec fond transparent');
      }
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleUploadDesign = async () => {
    if (!selectedFile) {
      toast.error('Veuillez sélectionner une image');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Session expirée, veuillez vous reconnecter');
        return;
      }
      
      // Upload image using the design-specific endpoint that removes red zones
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('category', 'designs');
      formData.append('remove_red', 'true'); // Auto-remove red placeholder zones
      
      // Use special design upload endpoint that handles red zone removal
      const uploadResponse = await fetch(`${API_URL}/api/media/upload-design`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (!uploadResponse.ok) {
        // Fallback to regular upload if design endpoint fails
        const fallbackFormData = new FormData();
        fallbackFormData.append('file', selectedFile);
        fallbackFormData.append('category', 'designs');
        
        const fallbackResponse = await fetch(`${API_URL}/api/media/upload`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: fallbackFormData
        });
        
        if (!fallbackResponse.ok) {
          throw new Error('Échec de l\'upload');
        }
        
        const fallbackData = await fallbackResponse.json();
        var imageUrl = fallbackData.url;
      } else {
        const uploadData = await uploadResponse.json();
        var imageUrl = uploadData.url;
        
        // Show notification about red pixels removed
        if (uploadData.red_pixels_removed > 0) {
          toast.info(`Zone rouge supprimée automatiquement (${uploadData.red_pixels_removed.toLocaleString()} pixels)`);
        }
      }
      
      // Create design entry
      const designResponse = await fetch(`${API_URL}/api/designs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          reservation_id: reservationId,
          image_url: imageUrl,
          format: selectedFormat,
          notes: uploadNotes,
          send_email: sendEmail,
          email_message: emailMessage
        })
      });
      
      if (designResponse.ok) {
        toast.success('Design soumis avec succès ! La zone rouge a été rendue transparente.');
        setUploadDialogOpen(false);
        resetUploadForm();
        loadDesigns();
        if (onUpdate) onUpdate();
      } else {
        throw new Error('Échec de la soumission');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const resetUploadForm = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setSelectedFormat('portrait');
    setUploadNotes('');
    setEmailMessage('');
  };

  const handleDownload = async (design) => {
    try {
      const response = await fetch(design.image_url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `design-${design.format}-${design.id}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  // Count approved designs
  const approvedCount = designs.filter(d => d.status === 'approved').length;
  const pendingCount = designs.filter(d => d.status === 'pending').length;

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500 mx-auto" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <FiImage className="h-5 w-5" />
              Designs / Cadres photo
            </CardTitle>
            <div className="flex items-center gap-2">
              <Badge variant="outline">
                {approvedCount}/{requiredModels} validés
              </Badge>
              {pendingCount > 0 && (
                <Badge className="bg-yellow-100 text-yellow-800">
                  {pendingCount} en attente
                </Badge>
              )}
            </div>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Cette réservation nécessite {requiredModels} modèle{requiredModels > 1 ? 's' : ''} de design
          </p>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {/* Existing designs */}
          {designs.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {designs.map((design) => (
                <div 
                  key={design.id} 
                  className="border rounded-xl p-4 hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => { setPreviewDesign(design); setPreviewDialogOpen(true); }}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <Badge variant="secondary" className="mb-1">
                        {DESIGN_FORMATS[design.format]?.label || design.format}
                      </Badge>
                      <StatusBadge status={design.status} />
                    </div>
                    <div className="flex gap-1">
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={(e) => { e.stopPropagation(); handleDownload(design); }}
                      >
                        <FiDownload className="h-4 w-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={(e) => { e.stopPropagation(); setPreviewDesign(design); setPreviewDialogOpen(true); }}
                      >
                        <FiZoomIn className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Design thumbnail with demo */}
                  <div className="relative bg-gray-100 rounded-lg overflow-hidden" style={{ height: '200px' }}>
                    <DesignPreview 
                      designUrl={design.image_url} 
                      format={design.format}
                      showDemo={true}
                    />
                  </div>
                  
                  {design.notes && (
                    <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                      {design.notes}
                    </p>
                  )}
                  
                  <p className="text-xs text-muted-foreground mt-2">
                    Soumis le {new Date(design.created_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 bg-gray-50 rounded-xl">
              <FiImage className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500 mb-1">Aucun design soumis</p>
              <p className="text-sm text-gray-400">Uploadez le design pour validation par le client</p>
            </div>
          )}
          
          {/* Upload button */}
          <Button 
            onClick={() => setUploadDialogOpen(true)}
            className="w-full"
            disabled={approvedCount >= requiredModels}
          >
            <FiUpload className="h-4 w-4 mr-2" />
            {designs.length === 0 ? 'Uploader le design' : 'Ajouter un design'}
          </Button>
          
          {approvedCount >= requiredModels && (
            <p className="text-sm text-green-600 text-center">
              Tous les designs requis ont été validés
            </p>
          )}
        </CardContent>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Soumettre un design</DialogTitle>
          </DialogHeader>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left: Upload form */}
            <div className="space-y-4">
              {/* Format selection */}
              <div>
                <label className="text-sm font-medium mb-2 block">Format du design</label>
                <Select value={selectedFormat} onValueChange={setSelectedFormat}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choisir un format" />
                  </SelectTrigger>
                  <SelectContent>
                    {allowedFormats.map(format => (
                      <SelectItem key={format} value={format}>
                        <div className="flex items-center gap-2">
                          {format === 'video360' && <FiPlay className="h-4 w-4" />}
                          {DESIGN_FORMATS[format].label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  {DESIGN_FORMATS[selectedFormat]?.description}
                </p>
              </div>
              
              {/* File upload */}
              <div>
                <label className="text-sm font-medium mb-2 block">Image du design</label>
                <div 
                  className="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer hover:border-amber-500 transition-colors"
                  onClick={() => document.getElementById('design-file-input').click()}
                >
                  <input
                    id="design-file-input"
                    type="file"
                    accept="image/png,image/jpg,image/jpeg,image/webp"
                    className="hidden"
                    onChange={handleFileSelect}
                  />
                  <FiUpload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">
                    {selectedFile ? selectedFile.name : 'Cliquez ou glissez une image'}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    PNG avec fond transparent recommandé (max 10MB)
                  </p>
                </div>
              </div>
              
              {/* Notes */}
              <div>
                <label className="text-sm font-medium mb-2 block">Notes internes (optionnel)</label>
                <Textarea
                  value={uploadNotes}
                  onChange={(e) => setUploadNotes(e.target.value)}
                  placeholder="Notes pour votre équipe..."
                  rows={2}
                />
              </div>
              
              {/* Email notification */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sendEmail}
                    onChange={(e) => setSendEmail(e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm">
                    Envoyer un email au client ({reservation?.client_email || 'N/A'})
                  </span>
                </label>
                
                {sendEmail && (
                  <Textarea
                    value={emailMessage}
                    onChange={(e) => setEmailMessage(e.target.value)}
                    placeholder="Message pour le client (optionnel)..."
                    rows={2}
                  />
                )}
              </div>
            </div>
            
            {/* Right: Preview */}
            <div className="space-y-4">
              <label className="text-sm font-medium block">Aperçu du résultat</label>
              <div className="bg-gray-900 rounded-xl p-4 flex items-center justify-center min-h-[300px]">
                <DesignPreview 
                  designUrl={previewUrl}
                  format={selectedFormat}
                  showDemo={true}
                />
              </div>
              <p className="text-xs text-center text-muted-foreground">
                L'image de démo montre comment le cadre s'appliquera sur les photos/vidéos
              </p>
            </div>
          </div>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => { setUploadDialogOpen(false); resetUploadForm(); }}>
              Annuler
            </Button>
            <Button onClick={handleUploadDesign} disabled={uploading || !selectedFile}>
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Upload en cours...
                </>
              ) : (
                <>
                  <FiSend className="h-4 w-4 mr-2" />
                  Soumettre le design
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Aperçu du design
              {previewDesign && (
                <Badge variant="secondary">
                  {DESIGN_FORMATS[previewDesign.format]?.label || previewDesign.format}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {previewDesign && (
            <div className="space-y-4">
              <div className="bg-gray-900 rounded-xl p-6 flex items-center justify-center">
                <DesignPreview 
                  designUrl={previewDesign.image_url}
                  format={previewDesign.format}
                  showDemo={true}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <StatusBadge status={previewDesign.status} />
                  <p className="text-sm text-muted-foreground mt-1">
                    Soumis le {new Date(previewDesign.created_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>
                <Button onClick={() => handleDownload(previewDesign)}>
                  <FiDownload className="h-4 w-4 mr-2" />
                  Télécharger
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
